<template>
  <div class="flex w-full">
    <import-listing
      ref="importListing"
      class="pending-leads-listing"
      :importSourceUrl="importSourceUrl"
      :importLeadType="importLeadType"
      :importTableColumns="importedUsersTableColumns"
      :importTableOptions="importedUsersTableOptions"
      :percentSwitch="percentSwitch"
      :showEmptyState="false"
      @downloadCsvOfLeads="downloadCsvOfLeads"
      @rejectLeadMethod="rejectLeadMethod"
      @approveAllLeadsInCsv="approveAllLeadsInCsv"
      @approveGoodLeadsInCsv="approveGoodLeadsInCsv"
      @showResponsesForThisCsv="showResponsesForThisCsv"
      @showMappingsForThisCsv="showMappingsForThisCsv"
      @showGatewaysForThisCsv="showGatewaysForThisCsv"
      @showImportsForThisUser="showImportsForThisUser"
    >
    </import-listing>
  </div>
</template>

<script>
import axios from 'axios';
import ImportListing from '@/components/import-listing.vue';
import demoModeValuesMixin from '@/mixin/demoModeValuesMixin.js';

let self;

export default {
  middleware: 'auth',
  metaInfo() {
    return {
      title: this.$t('frontend_groovemail_import_user_listing_pending_status'),
    };
  },

  mixins: [demoModeValuesMixin],

  props: {
    importSourceUrl: {
      required: true,
    },
    importLeadType: {
      required: false,
    },
    percentSwitch: {
      required: false,
    },
  },

  data() {
    return {
      importedUsersTableColumns: [
        'user_email',
        'appr_rej_count',
        'csv_name',
        'total_leads',
        'block_lead',
        'bounce_lead',
        'suppression_lead',
        'good_leads',
        'actions',
        'view',
      ],
      importedUsersTableOptions: {
        perPage: 10,
        debounce: 1000,
        alwaysShowPerPageSelect: true,
        perPageValues: [10, 25, 50, 100],
        pagination: { chunk: 5 },
        sortIcon: {
          base: 'fa',
          is: 'fa-sort',
          up: 'fa-sort-asc',
          down: 'fa-sort-desc',
        },
        texts: {
          filterPlaceholder: this.$t('frontend_groovemail_tables_search_query'),
        },
        sortable: [
          'csv_name',
          'user_email',
          'block_lead',
          'bounce_lead',
          'suppression_lead',
          'total_leads',
          'good_leads',
        ],
        filterable: ['csv_name'],
        headings: {
          user_email: () =>
            this.$t(
              'frontend_groovemail_admin_import_user_approval_user_email_header'
            ),
          appr_rej_count: () =>
            this.$t(
              'frontend_groovemail_admin_import_user_approval_user_appr_rej_count'
            ),
          csv_name: () =>
            this.$t(
              'frontend_groovemail_admin_import_user_approval_csv_name_header'
            ),
          total_leads: () =>
            this.$t(
              'frontend_groovemail_admin_import_user_approval_total_leads_header'
            ),
          block_lead: () =>
            this.$t(
              'frontend_groovemail_admin_import_user_approval_blocked_leads_header'
            ),
          bounce_lead: () =>
            this.$t(
              'frontend_groovemail_admin_import_user_approval_bounce_leads_header'
            ),
          suppression_lead: () =>
            this.$t(
              'frontend_groovemail_admin_import_user_approval_suppression_leads_header'
            ),
          good_leads: () =>
            this.$t(
              'frontend_groovemail_admin_import_user_approval_good_leads_header'
            ),
        },
        requestFunction(data) {
          const requestParams = { ...data, ascending: 1 - data.ascending };
          requestParams.approved = 0;
          if (!(requestParams && requestParams.orderBy)) {
            requestParams.orderBy = 'created_at';
            requestParams.ascending = 0;
          }
          const self = this;
          return axios
            .get(self.url, {
              params: requestParams,
            })
            .catch((e) => {
              self.dispatch('error', e);
            });
        },
        responseAdapter({ data }) {
          if(self.demoMode) {
            const newData = data?.data.map((obj) => ({
              ...obj,
              user_email: self.basicEmailText,
            }));

            return {
              data: newData,
              count: newData.length,
            };
          }

          return {
            data: data.data,
            count: data.total,
          };
        },
      },
    };
  },

  components: {
    ImportListing,
  },

  created() {
    self = this;
  },

  watch: {
    // Used to refresh import table data table when demo mode changes
    demoMode() {
      this.$refs.importListing.$refs.serverTable.$refs.importTable.refresh();
    },
  },

  computed: {
    // Used to return demoMode value from store.
    demoMode() {
      return this.$store.state.demoMode.isDemoMode;
    },
  },

  methods: {
    /**
     * Emits event to trigger downloading of csv of different type of contacts (all, good, bad).
     * @param {object} csvData data related to csv file
     * @param {string} url url for downloading data
     * @param {number} type type of contact
     *    0 - bad contacts
     *    1 - good contacts
     *    2 - all contacts
     */
    downloadCsvOfLeads(csvData, url, type) {
      this.$emit('downloadCsvOfLeads', csvData, url, type);
    },
	  
    /**
     * Emits event that triggers rejection of imported contacts.
     * @param {object} csvData data related to csv file
     */
    rejectLeadMethod(csvData,importStatus) {
      this.$emit('rejectLeadMethod', csvData,importStatus);
    },
	  
    /**
     * Emits event that triggers approval of all imported contacts.
     * @param {object} csvData data related to csv file
     */
    approveAllLeadsInCsv(csvData) {
      this.$emit('approveAllLeadsInCsv', 'allLeads', csvData, this.importLeadType);
    },
	  
    /**
     * Emits event that triggers approval of good imported contacts only.
     * @param {object} csvData data related to csv file
     */
    approveGoodLeadsInCsv(csvData) {
      this.$emit('approveGoodLeadsInCsv', 'goodLeads', csvData, this.importLeadType);
    },

    /**
     * Emits event to trigger showing import responses.
     * @param {object} csvData data related to csv file
     */
    showResponsesForThisCsv(csvData) {
      this.$emit('showResponsesForThisCsv', csvData);
    },
	
    /**
     * Emits event to trigger showing import mappings.
     * @param {object} csvData data related to csv file
     */
    showMappingsForThisCsv(csvData) {
      this.$emit('showMappingsForThisCsv', csvData);
    },
	  
    /**
     * Emits event to trigger showing sender gateway details.
     * @param {object} csvData data related to csv file
     */
    showGatewaysForThisCsv(csvData) {
      this.$emit('showGatewaysForThisCsv', csvData);
    },
	  
    /**
     * Emits event to trigger showing imports for a specific user.
     * @param {string} type type of imported contacts
     * @param {string} email email of contact who imported
     */
    showImportsForThisUser(type, email) {
      this.$emit('showImportsForThisUser', type, email);
    },
  },
};
</script>


<style lang="scss">
.pending-leads-listing .groovemail-common-import-leads-table #gd-server-table-view {
  .VueTables__table th:nth-last-child(2) span {
    display: none;
  }

  table {
    th:nth-child(1),
    td:nth-child(1),
    th:nth-child(3),
    td:nth-child(3) {
      width: 17%;
      word-break: break-all;
    }
    th:nth-child(2),
    td:nth-child(2) {
      width: 9%;
    }
    th:nth-child(4),
    td:nth-child(5),
    th:nth-child(5),
    td:nth-child(5),
    th:nth-child(6),
    td:nth-child(6),
    th:nth-child(8),
    td:nth-child(8) {
      width: 9%;
    }
    th:nth-child(7),
    td:nth-child(7) {
      width: 11%;
    }
    th:nth-child(9),
    td:nth-child(9) {
      width: 5%;
    }
    th:nth-child(10),
    td:nth-child(10) {
      width: 5%;
    }
  }
}
</style>
